import * as XLSX from 'xlsx-js-style';
import { ManufacturerItem, ManufacturerStatus, ManufacturerStatusLabels } from '../types/manufacturer-types';

interface ExportRow {
  'ID': number;
  'Manufacturer Name': string;
  'Model Name': string;
  'Status': string;
  'AC/DC': string;
  'OCPP Tariff': string;
  'OCPP Cost': string;
  'Created Date': string;
  'Created By': string;
  'Updated Date': string;
  'Updated By': string;
}

/**
 * Format date for export
 */
const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format date for filename (YYYY-MM-DD)
 */
const formatDateForFilename = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate optimal column width based on content
 */
const calculateColumnWidth = (data: ExportRow[], key: keyof ExportRow, headerLength: number): number => {
  const maxLength = data.reduce((max, row) => {
    const cellLength = row[key]?.toString().length || 0;
    return Math.max(max, cellLength);
  }, headerLength);

  // Add some padding and cap at reasonable maximum
  return Math.min(Math.max(maxLength + 2, 10), 40);
};

/**
 * Export manufacturers data to Excel format
 */
export const exportManufacturersToExcel = (
  data: ManufacturerItem[],
  filename?: string
) => {
  if (!data || data.length === 0) {
    throw new Error('No manufacturer data to export');
  }

  // Map data to export rows
  const excelData: ExportRow[] = data.map((item) => ({
    'ID': item.id,
    'Manufacturer Name': item.manufacturerName,
    'Model Name': item.modelName || '-',
    'Status': ManufacturerStatusLabels[item.status as ManufacturerStatus] || 'Unknown',
    'AC/DC': item.isDc ? 'DC' : 'AC',
    'OCPP Tariff': item.isOcppTariff ? 'Yes' : 'No',
    'OCPP Cost': item.isOcppCost ? 'Yes' : 'No',
    'Created Date': formatDateTime(item.created),
    'Created By': item.createdByName || '-',
    'Updated Date': formatDateTime(item.updated),
    'Updated By': item.updatedByName || '-',
  }));

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Define headers
  const headers: (keyof ExportRow)[] = [
    'ID',
    'Manufacturer Name',
    'Model Name',
    'Status',
    'AC/DC',
    'OCPP Tariff',
    'OCPP Cost',
    'Created Date',
    'Created By',
    'Updated Date',
    'Updated By',
  ];

  // Auto-resize columns based on content
  const columnWidths = headers.map(header => ({
    wch: calculateColumnWidth(excelData, header, header.length)
  }));

  worksheet['!cols'] = columnWidths;

  // Make header row bold with styling
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    if (!cell) continue;

    // Apply bold font and center alignment to header cells
    cell.s = {
      font: {
        bold: true,
        sz: 11,
        color: { rgb: "000000" }
      },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
        wrapText: false
      },
      fill: {
        fgColor: { rgb: "E7E6E6" }
      },
      border: {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      }
    };
  }

  // Add auto-filter to header row
  worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${excelData.length + 1}` };

  // Add the worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Manufacturers');

  // Generate filename with current date if not provided
  const exportFilename = filename || `manufacturers-${formatDateForFilename(new Date())}.xlsx`;

  // Write and download the file
  XLSX.writeFile(workbook, exportFilename);
};
